import api from '@/shared/api/api';

// =============================================
// Landing Page Config
// =============================================

export const adminLandingApi = {
  /** Public: get landing page data */
  getPublic: () => api.get('/admin/landing/public').then((r: any) => r.data),

  /** Get landing settings */
  getSettings: () => api.get('/admin/landing/settings').then((r: any) => r.data),

  /** Update landing settings */
  updateSettings: (data: any) => api.put('/admin/landing/settings', data).then((r: any) => r.data),

  /** Seed default landing data */
  seed: () => api.post('/admin/landing/seed').then((r: any) => r.data),

  // Hero media
  getHeroMedia: () => api.get('/admin/landing/hero-media').then((r: any) => r.data),

  addHeroMedia: (data: any) => api.post('/admin/landing/hero-media', data).then((r: any) => r.data),

  uploadHeroMedia: (formData: any) =>
    api
      .post('/admin/landing/hero-media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r: any) => r.data),

  updateHeroMedia: (imageId: any, data: any) =>
    api.put(`/admin/landing/hero-media/${imageId}`, data).then((r: any) => r.data),

  removeHeroMedia: (imageId: any) => api.delete(`/admin/landing/hero-media/${imageId}`).then((r: any) => r.data),

  // Cards
  getCards: () => api.get('/admin/landing/cards').then((r: any) => r.data),

  createCard: (data: any) => api.post('/admin/landing/cards', data).then((r: any) => r.data),

  updateCard: (cardId: any, data: any) => api.put(`/admin/landing/cards/${cardId}`, data).then((r: any) => r.data),

  removeCard: (cardId: any) => api.delete(`/admin/landing/cards/${cardId}`).then((r: any) => r.data),

  uploadCardImage: (formData: any) =>
    api
      .post('/admin/landing/cards/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r: any) => r.data),
};

// =============================================
// Fidelity Config
// =============================================

export const adminFidelityApi = {
  /** Get fidelity tiers */
  getTiers: () => api.get('/admin/fidelity/tiers').then((r: any) => r.data),

  /** Update fidelity tiers */
  updateTiers: (tiers: any) => api.put('/admin/fidelity/tiers', { tiers }).then((r: any) => r.data),
};

// =============================================
// Image Search Config
// =============================================

export const adminImageSearchConfigApi = {
  /** Get config */
  get: () => api.get('/admin/image-search-config').then((r: any) => r.data),

  /** Update config */
  update: (config: any) => api.put('/admin/image-search-config', config).then((r: any) => r.data),

  /** Reset config */
  reset: () => api.post('/admin/image-search-config/reset').then((r: any) => r.data),
};

// =============================================
// Site Mode
// =============================================

export const adminSiteModeApi = {
  /** Get current site mode */
  get: () => api.get('/site-mode').then((r: any) => r.data),

  /** Public: subscribe to launch notifications */
  subscribeNotification: (data: any) => api.post('/site-mode/notifications', data).then((r: any) => r.data),

  /** Update site mode */
  update: (data: any) => api.put('/site-mode/admin', data).then((r: any) => r.data),

  /** Get mode history */
  getHistory: () => api.get('/site-mode/admin/history').then((r: any) => r.data),
};

// =============================================
// Chatbot Admin
// =============================================

export const adminChatbotApi = {
  /** Get chatbot stats */
  getStats: (range: any) => api.get('/chatbot/admin/stats', { params: { range } }).then((r: any) => r.data),

  /** Get conversations */
  getConversations: (range: any) =>
    api.get('/chatbot/admin/conversations', { params: { range } }).then((r: any) => r.data),

  /** Get AI models */
  getModels: () => api.get('/chatbot/admin/ai-models').then((r: any) => r.data),

  /** Set active AI model */
  setModel: (modelId: any) => api.post('/chatbot/admin/ai-model', { model_id: modelId }).then((r: any) => r.data),

  /** Get conversation history */
  getHistory: (sessionId: any) => api.get(`/chatbot/history/${sessionId}`).then((r: any) => r.data),

  /** Delete conversation */
  removeHistory: (sessionId: any) => api.delete(`/chatbot/history/${sessionId}`).then((r: any) => r.data),
};

// =============================================
// Chatbot Traces (Logs)
// =============================================

export const adminChatbotTracesApi = {
  /** Get trace sessions */
  getSessions: (params: any) => api.get('/admin/chatbot-traces/sessions', { params }).then((r: any) => r.data),

  /** Get trace stats */
  getStats: (days = 7) => api.get('/admin/chatbot-traces/stats', { params: { days } }).then((r: any) => r.data),

  /** Get single session detail */
  getSession: (sessionId: any) => api.get(`/admin/chatbot-traces/sessions/${sessionId}`).then((r: any) => r.data),

  /** Replay a session */
  replay: (data: any) => api.post('/admin/chatbot-traces/replay', data).then((r: any) => r.data),
};

// =============================================
// Partners
// =============================================

export const adminPartnerApi = {
  /** List partners */
  getAll: (params: any) => api.get('/admin/partners', { params }).then((r: any) => r.data),

  /** Get single partner */
  getById: (id: any) => api.get(`/admin/partners/${id}`).then((r: any) => r.data),

  /** Create partner */
  create: (formData: any) =>
    api
      .post('/admin/partners', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r: any) => r.data),

  /** Update partner */
  update: (id: any, formData: any) =>
    api
      .put(`/admin/partners/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r: any) => r.data),

  /** Update partner status */
  updateStatus: (id: any, status: any) =>
    api.patch(`/admin/partners/${id}/status`, null, { params: { status } }).then((r: any) => r.data),

  /** Soft delete partner */
  remove: (id: any) => api.delete(`/admin/partners/${id}`).then((r: any) => r.data),

  /** Restore deleted partner */
  restore: (id: any) => api.patch(`/admin/partners/${id}/restore`).then((r: any) => r.data),

  /** Permanently delete partner */
  removePermanently: (id: any) =>
    api.delete(`/admin/partners/${id}`, { params: { permanent: true } }).then((r: any) => r.data),
};

// =============================================
// Schema Registry
// =============================================

export const adminSchemaRegistryApi = {
  /** List schemas */
  getAll: (params: any) => api.get('/admin/schema-registry', { params }).then((r: any) => r.data),

  /** Get single schema */
  getById: (id: any) => api.get(`/admin/schema-registry/${id}`).then((r: any) => r.data),

  /** Create schema */
  create: (data: any) => api.post('/admin/schema-registry', data).then((r: any) => r.data),

  /** Update schema */
  update: (id: any, data: any) => api.put(`/admin/schema-registry/${id}`, data).then((r: any) => r.data),

  /** Delete schema */
  remove: (id: any) => api.delete(`/admin/schema-registry/${id}`).then((r: any) => r.data),

  /** Activate schema */
  activate: (id: any) => api.post(`/admin/schema-registry/${id}/activate`).then((r: any) => r.data),

  /** Deactivate schema */
  deactivate: (id: any) => api.post(`/admin/schema-registry/${id}/deactivate`).then((r: any) => r.data),

  /** Duplicate schema */
  duplicate: (id: any) => api.post(`/admin/schema-registry/${id}/duplicate`).then((r: any) => r.data),

  /** Get stats */
  getStats: () => api.get('/schema-registry/stats').then((r: any) => r.data),
};
